import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import type { PreferredFormat } from '../../types/api';
import { loadPreferences, savePreferences } from '../../utils/preferences';

export function Composer({
  onSend,
  disabled,
}: {
  onSend: (text: string, format: PreferredFormat) => void;
  disabled?: boolean;
}) {
  const initial = loadPreferences().defaultFormat;
  const [text, setText] = useState('');
  const [format, setFormat] = useState<PreferredFormat>(initial);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Mod+/ focuses the composer from anywhere
  useEffect(() => {
    function onKey(e: KeyboardEvent | globalThis.KeyboardEvent) {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && (e as globalThis.KeyboardEvent).key === '/') {
        (e as Event).preventDefault();
        textareaRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey as EventListener);
    return () => window.removeEventListener('keydown', onKey as EventListener);
  }, []);

  function submit(e?: FormEvent) {
    e?.preventDefault();
    const t = text.trim();
    if (!t || disabled) return;
    onSend(t, format);
    setText('');
    // Persist last-used format as the new default for future messages
    savePreferences({ defaultFormat: format });
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <form
      onSubmit={submit}
      // Transparent — inherits the persona-tinted session backdrop set
      // on the ConversationView root, so the composer reads as part of
      // the same continuous surface as the body, not a separate beige
      // strip floating at the bottom.
      className="sticky bottom-0 border-t border-slate-900/[0.06] bg-transparent p-3 sm:p-4"
    >
      <div className="mx-auto w-full max-w-3xl">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={disabled}
            placeholder="Ask something…"
            rows={1}
            className="field resize-none min-h-[48px] max-h-[180px] flex-1"
            aria-label="Message"
          />
          <button
            type="submit"
            disabled={disabled || !text.trim()}
            className="btn-primary aspect-square h-12 min-h-0 w-12 p-0"
            aria-label="Send"
          >
            <SendIcon />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
          <FormatPicker value={format} onChange={setFormat} />
          <span className="hidden sm:block text-slate-400">
            Enter to send · Shift+Enter for newline
          </span>
        </div>
      </div>
    </form>
  );
}

function FormatPicker({
  value,
  onChange,
}: {
  value: PreferredFormat;
  onChange: (v: PreferredFormat) => void;
}) {
  const options: Array<{ id: PreferredFormat; label: string }> = [
    { id: 'auto', label: 'Auto' },
    { id: 'text', label: 'Text' },
    { id: 'chart', label: 'Chart' },
  ];
  return (
    <div role="radiogroup" aria-label="Response format" className="flex gap-1">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={`rounded-full px-3 py-1 text-xs transition-colors ${
            value === o.id
              ? 'bg-wb-700 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
